/**
 * Where a Doc's layers sit on the canvas, precomputed once per Doc.
 *
 * The SVG animation components measure their subject at runtime with
 * `getBBox()`. The HTML path computes CSS from data instead, so the same
 * information comes from the model plus resting DOM measurements for hugged
 * axes. The model stores a layer's `x`/`y` relative to its parent, while
 * animations reason in canvas coordinates (a `move` from an edge travels to
 * the canvas edge). This walk resolves the two.
 */
import { textAnchorFractions } from "@hightouch/lib/ad-studio/html-editor/layer-css";
import {
  textHugsWidth,
  hugsHeightAxis,
  type Doc,
  type Layer,
} from "@hightouch/lib/ad-studio/html-editor/model";

/**
 * A box in canvas pixels: the HTML stand-in for an SVG element's `getBBox()`.
 * It is the box the viewer sees, so an anchored text layer's rect starts
 * `anchor.x * width` left of its model `x`.
 */
export interface LayerRect {
  x: number;
  y: number;
  width: number;
  height: number;
  /**
   * The layer's grow anchor as a fraction of its size, present only when
   * non-zero (see `textAnchorFractions`). The CSS box still grows from its
   * own left/top; the anchor translate then shifts it back, so a block that
   * changes the box size (resize) needs this to hold the visual center.
   */
  anchor?: { x: number; y: number };
}

export type LayerSize = Pick<LayerRect, "width" | "height">;

/** Geometry behind a block's automatic off-canvas travel and circle wipes. */
export interface LayerBounds {
  /** The layer's box, in canvas coordinates (not parent-relative). */
  rect: LayerRect;
  /** The canvas the layer sits on — the Doc's declared dimensions. */
  canvas: { width: number; height: number };
}

export interface MotionGeometry {
  /** The canvas the layers sit on — the Doc's declared dimensions. */
  canvas: { width: number; height: number };
  /**
   * Canvas-absolute box per layer id. A layer is absent when the model doesn't
   * determine its placement (see {@link docMotionGeometry}).
   */
  rects: ReadonlyMap<string, LayerRect>;
}

/** One layer's bounds for the block evaluator, or undefined when unmeasured. */
export function layerBounds(
  geometry: MotionGeometry | undefined,
  layerId: string,
): LayerBounds | undefined {
  const rect = geometry?.rects.get(layerId);
  return geometry && rect ? { rect, canvas: geometry.canvas } : undefined;
}

/**
 * Resolve every layer's canvas-absolute box.
 *
 * Children of a flex auto-layout group are omitted, along with their whole
 * subtree: flexbox owns their placement, so their model `x`/`y` is not a
 * position and adding it to the parent's offset would produce a confidently
 * wrong box. Consumers treat a missing entry as "unmeasured" rather than
 * trusting a fabricated one.
 */
export function docMotionGeometry(
  doc: Doc,
  sizes: ReadonlyMap<string, LayerSize> = new Map(),
): MotionGeometry {
  const rects = new Map<string, LayerRect>();
  const walk = (
    layers: readonly Layer[],
    offsetX: number,
    offsetY: number,
    placed: boolean,
  ): LayerRect[] => {
    const bounds: LayerRect[] = [];
    for (const layer of layers) {
      const measured = sizes.get(layer.id);
      const width = textHugsWidth(layer) ? measured?.width : layer.width;
      const height = hugsHeightAxis(layer) ? measured?.height : layer.height;
      const anchor = textAnchorFractions(layer);
      const x = offsetX + layer.x - anchor.x * (width ?? 0);
      const y = offsetY + layer.y - anchor.y * (height ?? 0);
      let rect: LayerRect = { x, y, width: width ?? 0, height: height ?? 0 };
      if (anchor.x || anchor.y) rect.anchor = anchor;
      if (layer.type === "group") {
        const isFlex =
          layer.extraStyles?.display === "flex" ||
          layer.extraStyles?.display === "inline-flex";
        const childBounds = walk(layer.children, x, y, placed && !isFlex);
        if (placed && !isFlex && childBounds.length > 0) {
          const union = unionRects(childBounds);
          rect = {
            x: layer.width > 0 ? x : union.x,
            y: layer.height > 0 ? y : union.y,
            width: layer.width > 0 ? layer.width : union.width,
            height: layer.height > 0 ? layer.height : union.height,
          };
        }
      }
      if (placed && width !== undefined && height !== undefined) {
        rects.set(layer.id, rect);
        bounds.push(rect);
      }
    }
    return bounds;
  };
  walk(doc.layers, 0, 0, true);
  return { canvas: { width: doc.width, height: doc.height }, rects };
}

function unionRects(rects: readonly LayerRect[]): LayerRect {
  const left = Math.min(...rects.map((rect) => rect.x));
  const top = Math.min(...rects.map((rect) => rect.y));
  const right = Math.max(...rects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}
